import { isDefined } from '../../../utils/variables';
import { CoordinateManager } from '../../coordinate-manager';
import { Coordinates } from '../../utilities/math-helpers';
import { SkyviewOrthographicCamera } from '../skyview-orthographic-camera';

export class CameraProjectionLimitGuard {
  private extents!: { min: Coordinates; max: Coordinates };

  private width!: number;
  private height!: number;
  private maxZoom!: number;

  private defineCameraFrustumLimit() {
    // Without a coordinate system getProjectionRange() produces a meaningless
    // degree-valued range — skip the frustum limit and let the camera move freely.
    if (!CoordinateManager.getInstance().hasCoordinateSystem()) {
      return;
    }

    this.extents = CoordinateManager.getInstance().getProjectionRange();
    this.calculateMaxZoom();
  }

  updateAspectRatio(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.calculateMaxZoom();
  }

  resolveMaxZoom(desiredZoom: number) {
    return Math.min(this.maxZoom ?? desiredZoom, desiredZoom);
  }

  applyCameraMovementCorrection(camera: SkyviewOrthographicCamera) {
    if (!isDefined(this.extents)) {
      this.defineCameraFrustumLimit();
    }

    // No valid extents means no coordinate system — skip clamping.
    if (!isDefined(this.extents)) {
      return;
    }

    const desiredPosition = this.clampCameraPositionWithinBoundary(camera);
    const movement = desiredPosition.sub(camera.position);

    if (movement.length() > Number.EPSILON) {
      camera.move(movement);
    }
  }

  private clampCameraPositionWithinBoundary(camera: SkyviewOrthographicCamera) {
    const halfViewSize = camera.getViewSize().multiplyScalar(0.5);
    return camera.position
      .clone()
      .clamp(
        CoordinateManager.getInstance()
          .projectCoordsToLocal(this.extents.min)
          .clone()
          .add(halfViewSize),
        CoordinateManager.getInstance()
          .projectCoordsToLocal(this.extents.max)
          .clone()
          .sub(halfViewSize),
      );
  }

  private calculateMaxZoom() {
    if (!isDefined(this.extents) || !isDefined(this.width)) {
      return;
    }

    this.maxZoom = (this.extents.max.easting - this.extents.min.easting) / 2.0 / (this.width / 2.0);
  }
}
